import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";
import Swal from "sweetalert2";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import * as FingerprintJS from "@fingerprintjs/fingerprintjs";

// Define prop types for row data
const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  memberName: PropTypes.string,
  fingerprintsRegistered: PropTypes.number,
  isRegistered: PropTypes.bool,
  originalData: PropTypes.shape({
    member: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      first_name: PropTypes.string,
      surname: PropTypes.string,
      nationality: PropTypes.string,
    }),
    biometrics: PropTypes.array,
  }),
});

// Cell Components
const SerialCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="medium">
    {value}
  </MDTypography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const MemberCell = ({ value, row }) => (
  <MDBox display="flex" alignItems="center" lineHeight={1}>
    <MDAvatar name={value} size="sm" />
    <MDBox ml={2} lineHeight={1}>
      <MDTypography display="block" variant="button" fontWeight="medium">
        {value}
      </MDTypography>
      <MDTypography variant="caption">
        {row.original.originalData.member?.nationality || "Member"}
      </MDTypography>
    </MDBox>
  </MDBox>
);
MemberCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({ original: rowPropTypes }).isRequired,
};

const RegistrationStatusCell = ({ value }) => (
  <MDBox display="flex" justifyContent="center">
    {value ? (
      <CheckIcon color="success" fontSize="medium" />
    ) : (
      <CloseIcon color="error" fontSize="medium" />
    )}
  </MDBox>
);
RegistrationStatusCell.propTypes = { value: PropTypes.bool.isRequired };

const FingerprintsCountCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="medium">
    {value} fingerprints
  </MDTypography>
);
FingerprintsCountCell.propTypes = { value: PropTypes.number.isRequired };

// Define ActionsCell component outside the hook
const ActionsCell = ({ row, handleRegisterFingerprint }) => {
  const member = row.original.originalData?.member || row.original;

  return (
    <MDBox display="flex" justifyContent="center" gap={1}>
      <MDButton
        variant="gradient"
        color={member.biometrics?.length > 0 ? "info" : "success"}
        size="small"
        onClick={() => handleRegisterFingerprint(member)}
      >
        <Icon>fingerprint</Icon>
        &nbsp;{member.biometrics?.length > 0 ? "Add More" : "Register"}
      </MDButton>
    </MDBox>
  );
};
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: rowPropTypes }).isRequired,
  handleRegisterFingerprint: PropTypes.func.isRequired,
};

export default function useBiometricRegistrationData() {
  const { user, church } = useAuth();
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [fingerIndex, setFingerIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleRegisterFingerprint = (member) => {
    // Extract the member data from either the nested structure or direct member object
    const memberData = member.originalData?.member || member;
    console.log("Member data to register:", memberData); // Debug log
    setSelectedMember(memberData);
    setRegistrationModalOpen(true);
  };

  // Create the ActionsWrapper component inside the hook to access handleRegisterFingerprint
  const ActionsWrapper = ({ row }) => (
    <ActionsCell row={row} handleRegisterFingerprint={handleRegisterFingerprint} />
  );
  ActionsWrapper.propTypes = {
    row: PropTypes.shape({ original: rowPropTypes }).isRequired,
  };

  const [biometricData, setBiometricData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      { Header: "MemberID", accessor: "memberId", align: "left", width: "5%" },
      { Header: "Member", accessor: "memberName", align: "left", width: "30%", Cell: MemberCell },
      {
        Header: "Registered",
        accessor: "isRegistered",
        align: "center",
        width: "15%",
        Cell: RegistrationStatusCell,
      },
      {
        Header: "Fingerprints",
        accessor: "fingerprintsRegistered",
        align: "center",
        width: "15%",
        Cell: FingerprintsCountCell,
      },
      {
        Header: "FingerIndex",
        accessor: "fingerIndex",
        align: "center",
        width: "15%",
        Cell: FingerprintsCountCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "20%",
        Cell: ActionsWrapper,
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const fetchMembersWithBiometrics = async () => {
    try {
      setBiometricData((prev) => ({ ...prev, isLoading: true }));
      const token = localStorage.getItem("authToken");
      const response = await fetch(`https://api.ofi-cia.com/api/biometrics/members/${church.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch members");

      const result = await response.json();
      const members = result.data || result.members || [];

      setBiometricData((prev) => ({
        ...prev,
        rows: members.map((member, index) => ({
          id: member.id,
          serial: index + 1,
          memberId: member.memberID,
          memberName: `${member.first_name} ${member.surname}`,
          isRegistered: member.is_registered,
          fingerprintsRegistered: member.fingerprints_registered,
          fingerIndex: member.finger_index,
          originalData: member,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      setBiometricData((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
      });
    }
  };

  const registerFingerprint = async () => {
    try {
      setIsProcessing(true);
      const token = localStorage.getItem("authToken");

      if (!selectedMember) {
        throw new Error("No member selected for enrollment");
      }

      // Get the member data - handle both direct member object and nested originalData
      const memberData = selectedMember.originalData?.member || selectedMember;

      // Safely construct the name
      const memberName =
        memberData.first_name && memberData.surname
          ? `${memberData.first_name} ${memberData.surname}`
          : memberData.memberName || "Unknown Member";

      // Prepare the request data
      const requestData = {
        member_id: memberData.id,
        user_id: memberData.id,
        name: memberName,
        template: "template_data_placeholder", // Replace with actual template
        finger_index: fingerIndex,
      };

      console.log("Enrollment request data:", requestData); // Debug log

      const response = await fetch("https://api.ofi-cia.com/api/enroll", {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      const responseData = await response.json();
      console.log("Enrollment response:", responseData); // Debug log

      if (!response.ok) {
        throw new Error(
          responseData.error ||
            responseData.message ||
            `Enrollment failed with status ${response.status}`
        );
      }

      if (!responseData.success) {
        throw new Error(responseData.message || "Enrollment completed with warnings");
      }

      Swal.fire({
        title: "Success!",
        text: `Fingerprint enrolled for ${memberName}`,
        icon: "success",
      });

      fetchMembersWithBiometrics();
      setRegistrationModalOpen(false);
    } catch (error) {
      console.error("Enrollment error:", error); // Debug log
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (church?.id) {
      fetchMembersWithBiometrics();
    }
  }, [church]);

  return {
    ...biometricData,
    registrationModalOpen,
    setRegistrationModalOpen,
    selectedMember,
    fingerIndex,
    setFingerIndex,
    isProcessing,
    registerFingerprint,
  };
}
